import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Clock,
  DollarSign,
  FileText,
  Briefcase,
  Search,
} from "lucide-react";
import { Project } from "@/types/project";
import { useUserStore } from "@/store/userStore";
import { useToast } from "@/hooks/use-toast";
import { FreelancerProfile, useJobApplyStore } from "@/store/jobApplyStore";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { APPLICATION_STATUS_MAP, APPLICATION_STATUS_WITH_KEY_MAPPING, APPLICATION_STATUS_WITH_VALUE, DURATION_TYPE, JOB_APPLICATION_STATUS } from "@/utils/constant";
import { API_END_POINTS, ROUTES } from "@/utils/routes";
import { authHeaders, Axios } from "@/utils/axios";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { JobApplications } from "@/store/jobApplyStore";
import ProfileHeader from "@/components/profile/ProfileHeader";
import Profile from "./Profile";
import Pagination from "@/components/ui/pagination-component";
import ContractDialogWrapper from "./InitiateContract";
import { ApplicationStatus, JobTabType } from "@/utils/enum";
import CircularDotsLoader from "@/components/common/CircularLoader";
import { capitalizeFirst, getImageUrl } from "@/lib/utils";

const JobDetails = () => {
  const ref = useRef(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("overview");
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isAiRecommFreeOpen, setIsAiRecommFreeOpen] = useState(false);
  const [isContractDialogOpen, setIsContractDialogOpen] = useState(false);
  const [freelancerApplicationID, setFreelancerApplicationID] = useState(null);
  const [applyJobData, setApplyJobData] = useState({
    jobId: "",
    freelancerId: "",
    userId: "",
    coverLetterText: "",
    coverLetterFileUrl: "",
    proposedBudget: "",
    coverLetterFile: undefined as File | undefined,
  });
  const isEditing = false;
  const [searchParams] = useSearchParams();
  const freelancerId = searchParams.get("freelancerId");
  const viewApplicationTab = searchParams.get("application");
  const { user } = useUserStore();
  const [freelancerDetail, setFreelancerDetails] = useState<JobApplications | null>(null)
  const [aiRecommFreeDetail, setAIRecommFreeDetail] = useState< FreelancerProfile| null>(null)
  const [isShowLoader, setIsShowLoader] = useState<boolean>(false);
  const { jobApplications, aiRecommendedFreelancerjob, jobdetails, fetchJobApplications, fetchAIRecommendedFreelancerJob,  updateApplicationStatus, fetchJobDetailsByFreelancer } =
    useJobApplyStore();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [projectDetails, setProjectDetails] = useState({
    tasks: [
      { id: 1, title: "Smart Contract Development", status: "completed" },
      { id: 2, title: "Frontend Integration", status: "in-progress" },
      { id: 3, title: "Security Audit", status: "pending" },
      { id: 4, title: "Documentation", status: "in-progress" },
      { id: 5, title: "User Testing", status: "pending" },
    ],
    team: [
      {
        id: 1,
        name: "Alex Johnson",
        role: "Lead Developer",
        avatar: "/placeholder.svg",
      },
      {
        id: 2,
        name: "Maria Rodriguez",
        role: "Blockchain Expert",
        avatar: "/placeholder.svg",
      },
      {
        id: 3,
        name: "David Kim",
        role: "Frontend Developer",
        avatar: "/placeholder.svg",
      },
    ],
    updates: [
      {
        id: 1,
        content: "Completed the first milestone of development",
        date: "Sept 30, 2023",
        user: "Alex Johnson",
      },
      {
        id: 2,
        content: "Frontend dashboard implemented with real-time data",
        date: "Oct 15, 2023",
        user: "David Kim",
      },
      {
        id: 3,
        content: "Integrated security protocols for asset management",
        date: "Nov 2, 2023",
        user: "Maria Rodriguez",
      },
    ],
    conversations: [
      {
        id: 1,
        sender: "Client",
        message: "Can we get an update on the integration?",
        time: "2 days ago",
      },
      {
        id: 2,
        sender: "Team",
        message:
          "We've completed about 70% of the integration. There were some challenges with the API that we've resolved.",
        time: "1 day ago",
      },
      {
        id: 3,
        sender: "Client",
        message: "Great! When can we expect to see a demo?",
        time: "1 day ago",
      },
      {
        id: 4,
        sender: "Team",
        message: "We can arrange a demo by the end of this week.",
        time: "12 hours ago",
      },
    ],
  });

  const getData = async () => {
    await fetchJobDetailsByFreelancer(id, user?.userId, user?.accessToken)
  }

  const loadNextPreAIRecommendedFreelancerJob = async (pageNumber: number) => {
    if (ref.current) {
      setCurrentPage(pageNumber);
      await fetchAIRecommendedFreelancerJob(id, user?.accessToken, itemsPerPage, pageNumber);
    }
  };

  useEffect(() => {
    if (id) {
      getData();
    }
  }, [id]);
  useEffect(() => {
    if (!id || !jobdetails || !jobdetails.job) return;

    setProject({
      id: parseInt(jobdetails?.job?._id),
      title: jobdetails?.job?.title || "Untitled Project",
      client: jobdetails?.enterprise?.name || "Unknown Client",
      status: jobdetails?.status,
      deadline: jobdetails?.job?.duration || "No Deadline",
      budget: (jobdetails?.job?.budget || 0).toString(),
      duration: jobdetails?.job?.duration || "N/A",
      durationUnit: jobdetails?.job?.durationUnit || 0,
      durationValue: jobdetails?.job?.durationValue || 0,
      skills: jobdetails?.job?.requiredSkills || [],
      description: jobdetails?.job?.description || "No description available",
      category: jobdetails?.job?.category || "",
      clientInfo: {
        name: jobdetails?.enterprise?.name,
        rating: jobdetails?.enterprise?.rating || 0,
        location: jobdetails?.enterprise?.location,
        jobsPosted: jobdetails?.job?.createdAt || "",
        verified: false,
      },
      postedDate: "Unknown",
      complexity: "Unknown",
      icon: <Briefcase size={20} />,
      proposals: 0,
      views: 0,
    });
    setApplyJobData((prev) => ({
      ...prev,
      freelancerId: jobdetails?.enterprise?._id,
      jobId: id,
      userId: user?.userId,
    }));

    setLoading(false);
  }, [jobdetails, id, user?.userId]);

  useEffect(() => {
    if (viewApplicationTab) {
      setActiveTab("application");
    }
  }, [viewApplicationTab]);

  const handleTabChange = async (tab: string) => {
    ref.current = false;
    setActiveTab(tab);
    if (tab === JobTabType?.Application) {
      await fetchJobApplications(id, user?.accessToken);
    }
    if (tab === JobTabType?.AI) {
      await fetchAIRecommendedFreelancerJob(id, user?.accessToken, itemsPerPage, currentPage);
      ref.current = true;
    }
  };

  const handleOpenViewAiRecommFreelancerModal = (freelancerData) => {
    setAIRecommFreeDetail(freelancerData);
    setIsAiRecommFreeOpen(true);
  }

  const backToAIRecommFreelancer = (freelancerId: string) => {
    const freelancer = freelancerId
    navigate(`/job-details/${jobdetails?.job?._id}`);
    setActiveTab('ai');
    handleTabChange('ai');
    const findMatch = aiRecommendedFreelancerjob?.results?.filter(value => value?.freelancer?._id === freelancer);
    const newFreelancerData = findMatch[0]?.freelancer;
    handleOpenViewAiRecommFreelancerModal(newFreelancerData);
  };

  if (freelancerId) {
    const freelancer = freelancerId
    const findMatch = aiRecommendedFreelancerjob?.results?.filter(value => value?.freelancer?._id === freelancer);
    const newFreelancerData = findMatch[0]?.freelancer;
    return <Profile backToAIRecommFreelancer={backToAIRecommFreelancer} newFreelancerData={newFreelancerData}/>;
  }

  if (loading) {
    return (
      <div className="container mx-auto p-4 max-w-7xl">
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container mx-auto p-4 max-w-7xl">
        <div className="flex flex-col items-center justify-center h-96">
          <FileText className="h-16 w-16 text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">Project Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The project you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate("/projects")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  const updateStatus = async (id: string, status: string) => {
    try {
      const res = await updateApplicationStatus(id, Number(status), user?.accessToken);
      toast({
        title: "Status updated successfully",
        variant: "success",
      });
    } catch (err) {
      toast({
        title: "Failed to update job applications status. please try again",
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: string | number) => {
    switch (Number(status)) {
      case 2 :
      case 4 :  
        return "bg-green-100 text-green-700 border-green-200";
      case 5 :
        return "bg-blue-100 text-blue-700 border-blue-200";
      case 3 :
        return "bg-purple-100 text-purple-700 border-purple-200";
      case 6 :
        return "bg-purple-100 text-red-700 border-red-200";
      case 7 :
        return "bg-purple-100 text-orange-700 border-orange-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const handleOpenViewModal = async (freelancerId) => {
    setIsOpen(true)
    let findObj = jobApplications?.find(item => item?.freelancer?._id === freelancerId);
    setFreelancerDetails(findObj);
    if (Number(findObj.status) === ApplicationStatus.APPLIED) {
      findObj.status = 2;
      setFreelancerDetails(findObj);
      await updateApplicationStatus(findObj._id, 2, user?.accessToken);
    }
  }

  const updateApplicationStatusAfterInitiateContract = async () => {
    let findObj = jobApplications?.find(item => item?.freelancer?._id === freelancerApplicationID);
    setFreelancerDetails(findObj);
    if (Number(findObj.status) !== ApplicationStatus.ACCEPTED && !findObj?.contractId) {
      findObj.status = 4;
      setFreelancerDetails(findObj);
      await updateApplicationStatus(findObj._id, 4, user?.accessToken);
    }
  }

  const handleCloseViewModal = () => {
    setFreelancerDetails(null)
    setIsOpen(false)
  }

  const handleInitiateChat = async () => {
    if (!freelancerDetail?._id) {
      toast({
        title: "No application selected",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsShowLoader(true);
      const response = await Axios.post(
        API_END_POINTS.INITIATE_CHAT,
        {
          applicationId: freelancerDetail._id,
          freelancerId: freelancerDetail.freelancer._id,
          enterpriseId: jobdetails.enterprise._id,
          jobId: jobdetails.job._id
        },
        authHeaders(user?.accessToken)
      );

      toast({
        title: "Chat initiated successfully!",
        variant: "success",
      });
      setIsShowLoader(false);
      navigate(
        `${ROUTES.MESSAGES}?conversationId=${response.data.conversationId}`
      );
    } catch (error) {
      console.error("Chat initiation error:", error);
      setIsShowLoader(false);
      toast({
        title: "Failed to initiate chat",
        description: "Please try again later.",
        variant: "destructive",
      });
    }
  };

  const handleCloseAiRecommFreelancerModal = () => {
    setIsAiRecommFreeOpen(false)
    setAIRecommFreeDetail(null)
  }

  const openContractInititeModal = (freelancerApplicationData: JobApplications) => {
    const freelancerApplicationID = freelancerApplicationData?.freelancer?._id;
    if (!freelancerApplicationData?.contractId) {
      handleCloseViewModal();
      setFreelancerApplicationID(freelancerApplicationID);
      setIsContractDialogOpen(true);
    } else {
      navigate("/contracts");
    }
  }

  const openDetailViewOfFreeLancer = (jobId: string | number, freelancer: FreelancerProfile) => {
    const encodedData = freelancer?._id
    navigate(ROUTES.ENTERPRISE_TO_FREELANCER(jobId, encodedData));
  }

  const handleInitiateChats = async (id: string) => {
    if (!id) {
      toast({
        title: "No application selected",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsShowLoader(true);
      const response = await Axios.post(
        API_END_POINTS.LIST_CONVERSATIONS,
        { otherUserId: id, jobId: jobdetails.job._id},
        authHeaders(user?.accessToken)
      );
      toast({
        title: "Chat initiated successfully!",
        variant: "success",
      });
      setIsShowLoader(false);
      navigate(
        `${ROUTES.MESSAGES}?conversationId=${response.data._id}`
      );
    } catch (error) {
      setIsShowLoader(false);
      console.error("Chat initiation error:", error);
      toast({
        title: "Failed to initiate chat",
        description: "Please try again later.",
        variant: "destructive",
      });
    }
  };

  const decodeHtmlEntities = (text?: string): string => {
    if (!text) return "";
    const txt = document.createElement("textarea");
    txt.innerHTML = text;
    return txt.value;
  }

  return (
    <div className="container mx-auto p-4 max-w-7xl">
      {/* Back button and Project Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate(ROUTES.MYJOBS)}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Projects
        </Button>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">{project.title}</h1>
            <div className="flex items-center mt-2 space-x-2">
              {/* <Badge
                variant={
                  project.status === "Completed"
                    ? "success"
                    : project.status === "In Progress"
                    ? "default"
                    : "secondary"
                }
              >
                {project.status}
              </Badge> */}
              <span className="text-sm text-muted-foreground">
                Project #{project.id}
              </span>
            </div>
          </div>
        </div>
      </div>

      {isShowLoader && <CircularDotsLoader className="absolute" text="Loading...." />}

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="space-y-4"
      >
        <TabsList className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="application">Applications</TabsTrigger>
          <TabsTrigger value="ai">AI Recommended Freelancer</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Project Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">
                  {decodeHtmlEntities(project?.description)}
                </p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-2">
                    <DollarSign className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="font-medium">Budget</p>
                      <p className="text-sm text-muted-foreground">
                        {project?.budget}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="font-medium">Duration</p>
                      <p className="text-sm text-muted-foreground">
                        {project?.durationValue} {DURATION_TYPE.find(d => Number(d.value) === project?.durationUnit)?.label}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <h4 className="text-sm font-medium mb-2">Skills Required</h4>
                  <div className="flex flex-wrap gap-2">
                    {project?.skills?.map((skill, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                        className="bg-muted/50"
                      >
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* <Card>
              <CardHeader>
                <CardTitle>Client Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-4 mb-4">
                  <Avatar className="h-12 w-12">
                    <AvatarImage
                      src={"/placeholder.svg"}
                      alt={project.clientInfo.name}
                    />
                    <AvatarFallback>
                      {project.clientInfo.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-medium">{project.clientInfo.name}</h3>
                    <div className="flex items-center mt-1">
                      <div className="text-yellow-500 flex">
                        {"★".repeat(Math.floor(project.clientInfo.rating))}
                        {"☆".repeat(5 - Math.floor(project.clientInfo.rating))}
                      </div>
                      <span className="ml-1 text-sm">
                        {project.clientInfo.rating}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Location:</span>
                    <span>{project.clientInfo.location}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Jobs Posted:</span>
                    <span>
                      {project.clientInfo.jobsPosted
                        ? dateFormat(String(project.clientInfo.jobsPosted))
                        : ""}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Verified:</span>
                    <span>{project.clientInfo.verified ? "Yes" : "No"}</span>
                  </div>
                </div>
                <Button className="w-full">View Client Profile</Button>
              </CardContent>
            </Card> */}
          </div>

          {/* <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Recent Updates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {projectDetails.updates.map((update) => (
                  <div
                    key={update.id}
                    className="border-l-2 border-muted pl-4 py-1"
                  >
                    <p className="font-medium">{update.content}</p>
                    <p className="text-sm text-muted-foreground">
                      {update.user} - {update.date}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card> */}
        </TabsContent>
        {user?.role === "Enterprise" && (
          <TabsContent value="application" className="space-y-4">
            {jobApplications?.length
              ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Proposed Budget</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jobApplications?.map((application) => (
                      <TableRow key={application?._id}>
                        <TableCell className="text-sm flex items-center">
                          <span className="mr-2 relative flex h-8 w-8 shrink-0 overflow-hidden rounded-full">
                            <img
                              src={getImageUrl(application?.freelancer?.profilePicture) || "https://ui-avatars.com/api/?name=" + encodeURIComponent(application?.freelancer?.name)}
                              alt={application?.freelancer?.name}
                              className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700"
                            />
                          </span>
                           {capitalizeFirst(application.freelancer.name).split(' ')[0]}</TableCell>
                        <TableCell className="text-sm">
                          {application?.coverLetterText ? application?.coverLetterText.charAt(0).toUpperCase() + application?.coverLetterText.slice(1) : "-"}
                        </TableCell>
                        <TableCell>${application?.proposedBudget}</TableCell>
                        <TableCell>
                          {Number(application?.status) === ApplicationStatus.REJECTED && <Badge className={getStatusColor(application.status)} >
                            {APPLICATION_STATUS_WITH_KEY_MAPPING[application?.status]}
                          </Badge>}
                          {Number(application?.status) !== ApplicationStatus.REJECTED &&<Select value={application?.status?.toString()} onValueChange={(e) => updateStatus(application?._id, e)}>
                            <SelectTrigger>
                              <Badge className={getStatusColor(application.status)} >
                                {APPLICATION_STATUS_WITH_KEY_MAPPING[application?.status]}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              {APPLICATION_STATUS_WITH_VALUE.map((status) => (
                                <SelectItem key={status.value} value={status.value}
                                  disabled={(Number(application?.status) !== ApplicationStatus.VIEWED && Number(status.value) === ApplicationStatus.VIEWED && Number(application?.status) !== ApplicationStatus.APPLIED)
                                    || (Number(application?.status) === ApplicationStatus.ACCEPTED && Number(status.value) === ApplicationStatus.INTERVIEW)
                                  }>
                                  {status.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>}
                        </TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm" onClick={() => handleOpenViewModal(application?.freelancer?._id)} disabled={Number(application?.status) === ApplicationStatus.REJECTED}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center p-6 text-gray-500">
                  <Search className="h-10 w-10 mx-auto mb-4 text-gray-300" />
                  <p>No application found.</p>
                </div>
              )}
          </TabsContent>
        )}
        {user?.role === "Enterprise" && (
          <TabsContent value="ai" className="space-y-4">
            {aiRecommendedFreelancerjob?.results?.length ? (
              <div
                className="rounded-lg border bg-card text-card-foreground shadow-sm"
              >
                <div className="space-y-1.5 p-6 flex flex-row items-center justify-between">
                  <h3 className="text-2xl font-semibold leading-none tracking-tight">
                    Project Team
                  </h3>
                  {/* <button className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 h-9 rounded-md px-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-user-plus h-4 w-4 mr-2"
                    >
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <line x1="19" x2="19" y1="8" y2="14"></line>
                      <line x1="22" x2="16" y1="11" y2="11"></line>
                    </svg>
                    Add Member
                  </button> */}
                </div>
                <div className="p-6 pt-0 space-y-4">
                  {aiRecommendedFreelancerjob.results.map((data, index) => (
                    <div key={index} className="flex items-center p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center space-x-3 flex-1">
                        <span className="relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full">
                          <img
                            className="aspect-square h-full w-full"
                            alt={data?.freelancer?.fullName}
                            src={getImageUrl(data?.freelancer?.profilePicture) || "https://ui-avatars.com/api/?name=" + encodeURIComponent(data?.freelancer?.fullName)}
                          />
                        </span>
                        <div>
                          <h3 className="font-medium">
                            {capitalizeFirst(data?.freelancer?.fullName).split(' ')[0]}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {data?.freelancer?.category}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-center w-48 text-center">
                        <p className="text-sm text-muted-foreground">
                          Experience: {data?.freelancer?.experience} years
                        </p>
                      </div>
                      <div className="flex-1 flex justify-end">
                        <button   onClick={() => handleOpenViewAiRecommFreelancerModal(data?.freelancer)} className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium h-9 rounded-md px-3 hover:bg-accent hover:text-accent-foreground">
                          View Profile
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
              : (
                <div className="text-center p-6 text-gray-500">
                  <Search className="h-10 w-10 mx-auto mb-4 text-gray-300" />
                  <p>No application found.</p>
                </div>
              )}
            { aiRecommendedFreelancerjob?.results?.length ?
              <Pagination
                totalPages={Math.ceil(aiRecommendedFreelancerjob?.total / aiRecommendedFreelancerjob?.limit)}
                onPageChange={loadNextPreAIRecommendedFreelancerJob}
                forcePage={(aiRecommendedFreelancerjob.page || 1) - 1}
                className="mt-6"
              /> : null
            }
          </TabsContent>
        )}
      </Tabs>
      <Dialog open={isOpen} onOpenChange={handleCloseViewModal} >
        <DialogContent className="max-w-3xl p-0 rounded-xl overflow-hidden">
          <div className="max-h-[90vh] overflow-y-auto p-6">
            <div className="space-y-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Avatar className="w-14 h-14 text-xl">
                    <AvatarImage src={getImageUrl(freelancerDetail?.freelancer?.profilePicture)} />
                    <AvatarFallback>
                      {freelancerDetail?.freelancer?.name
                        ? freelancerDetail?.freelancer?.name?.charAt(0)
                        : "A"}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-semibold text-xl">
                      {capitalizeFirst(freelancerDetail?.freelancer?.name.split(" ")[0])}
                    </div>
                  </div>
                </div>
                <Badge className={getStatusColor(freelancerDetail?.status)}>
                  {/* {freelancerDetail?.freelancer?.availability ? "Yes" : "No"} */}
                  {APPLICATION_STATUS_WITH_KEY_MAPPING[Number(freelancerDetail?.status)] || "Unknown Status"}
                </Badge>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-medium mb-2">
                  Freelancer application description
                </h3>
                <p className="text-gray-700">
                  {freelancerDetail?.coverLetterText}
                </p>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Timeline
                  </h3>
                  <p className="text-gray-900">{freelancerDetail?.durationValue}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Budget
                  </h3>
                  <p className="text-gray-900">$ {freelancerDetail?.proposedBudget}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Availability
                  </h3>
                  <p className="text-gray-900">Yes</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    Location
                  </h3>
                  <p className="text-gray-900">01/BS/13 - linton street, kolkata , 700019</p>
                </div>
              </div>
              <Separator />
            </div>
            <DialogFooter className="flex space-x-2">
              <Button onClick={() =>openContractInititeModal(freelancerDetail)}>
                {freelancerDetail?.contractId ? 'View Contract'  : 'Initiate Contract'}
              </Button>
              <Button
                onClick={handleInitiateChat} disabled={isShowLoader}
              >
                Chat
              </Button>
              <Button
                variant="outline"
                onClick={handleCloseViewModal}
              >
                Cancel
              </Button>
            </DialogFooter>
          </div>

        </DialogContent>
      </Dialog>
      {/* --------------------- view profile AI Recommended Freelancer popup --------------- */}
      <Dialog open={isAiRecommFreeOpen} onOpenChange={handleCloseAiRecommFreelancerModal} >
        <DialogContent className="max-w-lg p-0 rounded-xl overflow-hidden">
          <div className="max-h-[90vh] overflow-y-auto p-6">
            <ProfileHeader
              isEditing={isEditing}
              isFromAIRecommFreelancerView={true}
              formData={{
                ...aiRecommFreeDetail,
                experience: String(aiRecommFreeDetail?.experience),
                availability:
                  aiRecommFreeDetail?.availability
                    ? "available"
                    : "offline",
              }}
            />
            <DialogFooter className="flex space-x-2 pt-5">
              <Button onClick={() => openDetailViewOfFreeLancer(jobdetails?.job?._id,aiRecommFreeDetail)}>
                View Profile
              </Button>
              <Button onClick={() => handleInitiateChats(aiRecommFreeDetail?._id)} disabled={isShowLoader}>
                Initiate Chat
              </Button>
              <Button
                variant="outline"
                onClick={handleCloseAiRecommFreelancerModal}
              >
                Close
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
      {/* -------------------- initiate contract popup ----------------------- */}
      <ContractDialogWrapper
        open={isContractDialogOpen}
        onOpenChange={setIsContractDialogOpen}
        updateApplicationStatusAfterInitiateContract={updateApplicationStatusAfterInitiateContract}
        freelancerApplicationID={freelancerApplicationID}
        jobdetailsId={jobdetails?.job?._id}
      ></ContractDialogWrapper>
    </div>
  );
};

export default JobDetails;
